import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Facebook, Instagram, Linkedin, Youtube, TrendingUp,
  Plus, Loader2, BarChart3, ArrowUp, ArrowDown, Minus,
  Clock, ExternalLink, Heart, MessageCircle, Share2, Eye,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import SEO from '@/components/SEO';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SocialMetric {
  id: string;
  platform: string;
  metric_date: string;
  followers: number;
  impressions: number;
  reach: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  engagement_rate: number;
  notes: string | null;
  created_at: string;
}

interface QueuePost {
  id: string;
  platform: string;
  content: string;
  status: string;
  image_url: string | null;
  post_url: string | null;
  scheduled_for: string | null;
  published_at: string | null;
  created_at: string;
  post_impressions: number;
  post_reach: number;
  post_likes: number;
  post_comments: number;
  post_shares: number;
  post_clicks: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLATFORMS = ['facebook', 'instagram', 'linkedin', 'linkedin_personal', 'linkedin_company', 'youtube'] as const;

const PLATFORM_CONFIG: Record<string, { icon: typeof Facebook; label: string; bg: string; border: string; text: string; chartColor: string }> = {
  facebook:          { icon: Facebook,  label: 'Facebook',               bg: 'bg-blue-50',   border: 'border-blue-300', text: 'text-blue-700',   chartColor: '#3b82f6' },
  instagram:         { icon: Instagram, label: 'Instagram',              bg: 'bg-pink-50',   border: 'border-pink-300', text: 'text-pink-700',   chartColor: '#ec4899' },
  linkedin:          { icon: Linkedin,  label: 'LinkedIn (Brian)',       bg: 'bg-amber-50',  border: 'border-amber-300', text: 'text-amber-800', chartColor: '#f59e0b' },
  linkedin_personal: { icon: Linkedin,  label: 'LinkedIn (Brian)',       bg: 'bg-amber-50',  border: 'border-amber-300', text: 'text-amber-800', chartColor: '#f59e0b' },
  linkedin_company:  { icon: Linkedin,  label: 'LinkedIn (Company)',     bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700', chartColor: '#6366f1' },
  youtube:           { icon: Youtube,   label: 'YouTube',                bg: 'bg-red-50',    border: 'border-red-300', text: 'text-red-700',     chartColor: '#ef4444' },
};

const TIME_RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const;

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = ['6a', '7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function startDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getWeekLabel(date: string): string {
  const d = new Date(date);
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay() + 1);
  return `${start.getMonth() + 1}/${start.getDate()}`;
}

function postEngagement(p: QueuePost): number {
  return (p.post_likes || 0) + (p.post_comments || 0) + (p.post_shares || 0);
}

function engagementRate(p: QueuePost): number {
  const eng = postEngagement(p);
  const imp = p.post_impressions || 0;
  return imp > 0 ? (eng / imp) * 100 : 0;
}

function buildFollowerGrowthData(metrics: SocialMetric[]): Record<string, unknown>[] {
  const byDate: Record<string, Record<string, number>> = {};
  for (const m of metrics) {
    if (!byDate[m.metric_date]) byDate[m.metric_date] = {};
    byDate[m.metric_date][m.platform] = m.followers;
  }
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, platforms]) => ({ date: formatDate(date), ...platforms }));
}

function buildEngagementSummary(metrics: SocialMetric[]): { platform: string; likes: number; comments: number; shares: number }[] {
  const agg: Record<string, { likes: number; comments: number; shares: number }> = {};
  for (const m of metrics) {
    if (!agg[m.platform]) agg[m.platform] = { likes: 0, comments: 0, shares: 0 };
    agg[m.platform].likes += m.likes;
    agg[m.platform].comments += m.comments;
    agg[m.platform].shares += m.shares;
  }
  return PLATFORMS
    .filter(p => agg[p])
    .map(p => ({
      platform: PLATFORM_CONFIG[p]?.label || p,
      likes: agg[p].likes,
      comments: agg[p].comments,
      shares: agg[p].shares,
    }));
}

function buildPostingCadence(posts: QueuePost[]): { weeks: string[]; data: Record<string, Record<string, number>> } {
  const weeksSet = new Set<string>();
  const data: Record<string, Record<string, number>> = {};
  const published = posts.filter(p => p.status === 'published' && p.published_at);
  for (const p of published) {
    const week = getWeekLabel(p.published_at!);
    weeksSet.add(week);
    if (!data[p.platform]) data[p.platform] = {};
    data[p.platform][week] = (data[p.platform][week] || 0) + 1;
  }
  return { weeks: Array.from(weeksSet).sort(), data };
}

/** Build best-time-to-post heatmap from published posts with engagement data */
function buildBestTimeData(posts: QueuePost[]): Record<string, Record<string, number>> {
  const grid: Record<string, Record<string, { total: number; count: number }>> = {};
  for (const day of DAYS) {
    grid[day] = {};
    for (const hour of HOURS) grid[day][hour] = { total: 0, count: 0 };
  }

  const withMetrics = posts.filter(p => p.status === 'published' && p.published_at && postEngagement(p) > 0);
  for (const p of withMetrics) {
    const d = new Date(p.published_at!);
    const dayIdx = (d.getDay() + 6) % 7; // Mon=0
    const h = d.getHours();
    if (h < 6 || h > 20) continue;
    const day = DAYS[dayIdx];
    const hourLabel = HOURS[h - 6];
    if (day && hourLabel) {
      grid[day][hourLabel].total += postEngagement(p);
      grid[day][hourLabel].count += 1;
    }
  }

  // Convert to averages
  const result: Record<string, Record<string, number>> = {};
  for (const day of DAYS) {
    result[day] = {};
    for (const hour of HOURS) {
      const cell = grid[day][hour];
      result[day][hour] = cell.count > 0 ? Math.round(cell.total / cell.count) : 0;
    }
  }
  return result;
}

/** Growth velocity: weekly follower gains */
function buildGrowthVelocity(metrics: SocialMetric[]): { week: string; gained: number }[] {
  // Group by week, sum follower deltas
  const sorted = [...metrics].filter(m => m.followers > 0).sort((a, b) => a.metric_date.localeCompare(b.metric_date));
  const byPlatform: Record<string, SocialMetric[]> = {};
  for (const m of sorted) {
    if (!byPlatform[m.platform]) byPlatform[m.platform] = [];
    byPlatform[m.platform].push(m);
  }

  const weeklyGains: Record<string, number> = {};
  for (const plat of Object.values(byPlatform)) {
    for (let i = 1; i < plat.length; i++) {
      const week = getWeekLabel(plat[i].metric_date);
      const delta = plat[i].followers - plat[i - 1].followers;
      weeklyGains[week] = (weeklyGains[week] || 0) + delta;
    }
  }

  return Object.entries(weeklyGains)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([week, gained]) => ({ week: `Wk ${week}`, gained }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SocialReporting() {
  const { toast } = useToast();
  const [range, setRange] = useState<number>(30);
  const [metrics, setMetrics] = useState<SocialMetric[]>([]);
  const [posts, setPosts] = useState<QueuePost[]>([]);
  const [loading, setLoading] = useState(true);

  // Manual entry dialog
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryPlatform, setEntryPlatform] = useState<string>('facebook');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryFields, setEntryFields] = useState({ followers: '', impressions: '', reach: '', likes: '', comments: '', shares: '', clicks: '', engagement_rate: '' });
  const [saving, setSaving] = useState(false);

  // ── Data Fetching ──

  const fetchData = useCallback(async () => {
    setLoading(true);
    const from = startDate(range);

    const [metricsRes, postsRes] = await Promise.allSettled([
      (supabase as any)
        .from('social_channel_metrics')
        .select('*')
        .gte('metric_date', from)
        .order('metric_date', { ascending: true }),
      supabase
        .from('social_content_queue')
        .select('id, platform, content, status, image_url, post_url, scheduled_for, published_at, created_at, post_impressions, post_reach, post_likes, post_comments, post_shares, post_clicks')
        .gte('created_at', from)
        .order('created_at', { ascending: false } as any)
        .limit(500),
    ]);

    if (metricsRes.status === 'fulfilled' && !metricsRes.value.error) {
      setMetrics((metricsRes.value.data || []) as SocialMetric[]);
    }
    if (postsRes.status === 'fulfilled' && !postsRes.value.error) {
      setPosts((postsRes.value.data || []) as QueuePost[]);
    }

    setLoading(false);
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived Data ──

  const publishedPosts = useMemo(() => posts.filter(p => p.status === 'published'), [posts]);

  const topPosts = useMemo(() =>
    [...publishedPosts]
      .sort((a, b) => postEngagement(b) - postEngagement(a))
      .slice(0, 20),
    [publishedPosts],
  );

  const platformStats = useMemo(() => {
    const stats: Record<string, { published: number; total: number; approved: number }> = {};
    for (const p of posts) {
      if (!stats[p.platform]) stats[p.platform] = { published: 0, total: 0, approved: 0 };
      stats[p.platform].total++;
      if (p.status === 'published') stats[p.platform].published++;
      if (p.status === 'approved') stats[p.platform].approved++;
    }
    return stats;
  }, [posts]);

  const latestMetricsByPlatform = useMemo(() => {
    const latest: Record<string, SocialMetric> = {};
    const earliest: Record<string, SocialMetric> = {};
    for (const m of metrics) {
      if (!latest[m.platform] || m.metric_date > latest[m.platform].metric_date) latest[m.platform] = m;
      if (!earliest[m.platform] || m.metric_date < earliest[m.platform].metric_date) earliest[m.platform] = m;
    }
    return { latest, earliest };
  }, [metrics]);

  const followerGrowthData = useMemo(() => buildFollowerGrowthData(metrics), [metrics]);
  const engagementSummary = useMemo(() => buildEngagementSummary(metrics), [metrics]);
  const cadence = useMemo(() => buildPostingCadence(posts), [posts]);
  const bestTimeData = useMemo(() => buildBestTimeData(publishedPosts), [publishedPosts]);
  const growthVelocity = useMemo(() => buildGrowthVelocity(metrics), [metrics]);

  // ── Manual Entry ──

  const saveMetrics = async () => {
    setSaving(true);
    try {
      const row = {
        platform: entryPlatform,
        metric_date: entryDate,
        followers: parseInt(entryFields.followers) || 0,
        impressions: parseInt(entryFields.impressions) || 0,
        reach: parseInt(entryFields.reach) || 0,
        likes: parseInt(entryFields.likes) || 0,
        comments: parseInt(entryFields.comments) || 0,
        shares: parseInt(entryFields.shares) || 0,
        clicks: parseInt(entryFields.clicks) || 0,
        engagement_rate: parseFloat(entryFields.engagement_rate) || 0,
      };

      const { error } = await (supabase as any)
        .from('social_channel_metrics')
        .upsert(row, { onConflict: 'platform,metric_date' });

      if (error) throw error;
      toast({ title: 'Metrics saved', description: `${PLATFORM_CONFIG[entryPlatform]?.label} for ${entryDate}` });
      setEntryOpen(false);
      setEntryFields({ followers: '', impressions: '', reach: '', likes: '', comments: '', shares: '', clicks: '', engagement_rate: '' });
      fetchData();
    } catch (err: any) {
      toast({ title: 'Error saving metrics', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──

  return (
    <>
      <SEO title="Social Reporting | Admin" noIndex />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">Social Reporting</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {TIME_RANGES.map(r => (
                <Button key={r.label} variant={range === r.days ? 'default' : 'ghost'} size="sm" onClick={() => setRange(r.days)}>
                  {r.label}
                </Button>
              ))}
            </div>
            <Button size="sm" onClick={() => setEntryOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add Metrics
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {PLATFORMS.map(p => <Skeleton key={p} className="h-32" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {PLATFORMS.map(p => {
              const cfg = PLATFORM_CONFIG[p];
              const Icon = cfg.icon;
              const stat = platformStats[p];
              const latest = latestMetricsByPlatform.latest[p];
              const earliest = latestMetricsByPlatform.earliest[p];
              const growthPct = latest && earliest && earliest.followers > 0
                ? ((latest.followers - earliest.followers) / earliest.followers * 100).toFixed(1)
                : null;
              const growthNum = growthPct ? parseFloat(growthPct) : 0;

              return (
                <Card key={p} className={`${cfg.bg} ${cfg.border} border`}>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${cfg.text}`} />
                      <span className={`text-sm font-medium ${cfg.text}`}>{cfg.label}</span>
                    </div>
                    {latest ? (
                      <>
                        <div className="text-2xl font-bold text-foreground">{latest.followers.toLocaleString()}</div>
                        <div className="flex items-center gap-1 text-xs mt-1">
                          {growthNum > 0 ? <ArrowUp className="h-3 w-3 text-green-600" /> :
                           growthNum < 0 ? <ArrowDown className="h-3 w-3 text-red-600" /> :
                           <Minus className="h-3 w-3 text-muted-foreground" />}
                          <span className={growthNum > 0 ? 'text-green-600' : growthNum < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                            {growthPct ? `${growthPct}%` : '0%'}
                          </span>
                          <span className="text-muted-foreground ml-1">followers</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">No metrics yet</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-2">
                      {stat?.published || 0} published / {stat?.total || 0} total posts
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="top-posts">Top Posts</TabsTrigger>
            <TabsTrigger value="best-time">Best Time to Post</TabsTrigger>
            <TabsTrigger value="cadence">Posting Cadence</TabsTrigger>
          </TabsList>

          {/* ── Overview Tab ── */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Follower Growth */}
              <Card className="col-span-1 lg:col-span-2 bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Follower Growth
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {followerGrowthData.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No follower data yet. Metrics auto-collect daily at 6am UTC.
                    </div>
                  ) : (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={followerGrowthData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Legend />
                          {PLATFORMS.map(p => (
                            <Line key={p} type="monotone" dataKey={p} name={PLATFORM_CONFIG[p].label} stroke={PLATFORM_CONFIG[p].chartColor} strokeWidth={2} dot={false} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Growth Velocity */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <ArrowUp className="h-5 w-5 text-green-600" />
                    Growth Velocity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {growthVelocity.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      Need 2+ weeks of data to show velocity.
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={growthVelocity}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Bar dataKey="gained" name="Followers Gained" radius={[4, 4, 0, 0]}
                            fill="hsl(var(--primary))" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Engagement Summary */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Engagement by Platform
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {engagementSummary.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No engagement data yet.
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={engagementSummary}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="platform" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Legend />
                          <Bar dataKey="likes" fill="#3b82f6" name="Likes" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="comments" fill="#10b981" name="Comments" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="shares" fill="#f59e0b" name="Shares" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Top Posts Tab ── */}
          <TabsContent value="top-posts">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Top Performing Posts</CardTitle>
              </CardHeader>
              <CardContent>
                {topPosts.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">No published posts with engagement data yet.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">Platform</TableHead>
                        <TableHead>Content</TableHead>
                        <TableHead className="w-[80px] text-center"><Eye className="h-3.5 w-3.5 mx-auto" /></TableHead>
                        <TableHead className="w-[60px] text-center"><Heart className="h-3.5 w-3.5 mx-auto" /></TableHead>
                        <TableHead className="w-[60px] text-center"><MessageCircle className="h-3.5 w-3.5 mx-auto" /></TableHead>
                        <TableHead className="w-[60px] text-center"><Share2 className="h-3.5 w-3.5 mx-auto" /></TableHead>
                        <TableHead className="w-[80px] text-right">Eng. Rate</TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topPosts.map(post => {
                        const cfg = PLATFORM_CONFIG[post.platform] || PLATFORM_CONFIG.facebook;
                        const Icon = cfg.icon;
                        const rate = engagementRate(post);
                        return (
                          <TableRow key={post.id}>
                            <TableCell>
                              <Badge variant="outline" className={`${cfg.bg} ${cfg.text} ${cfg.border}`}>
                                <Icon className="h-3 w-3 mr-1" />
                                {cfg.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-sm">
                              {post.content.substring(0, 100)}{post.content.length > 100 ? '...' : ''}
                            </TableCell>
                            <TableCell className="text-center text-sm tabular-nums">{(post.post_impressions || 0).toLocaleString()}</TableCell>
                            <TableCell className="text-center text-sm tabular-nums">{(post.post_likes || 0).toLocaleString()}</TableCell>
                            <TableCell className="text-center text-sm tabular-nums">{post.post_comments || 0}</TableCell>
                            <TableCell className="text-center text-sm tabular-nums">{post.post_shares || 0}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums font-medium">
                              {rate > 0 ? `${rate.toFixed(1)}%` : '-'}
                            </TableCell>
                            <TableCell>
                              {post.post_url && (
                                <a href={post.post_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                </a>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Best Time to Post Tab ── */}
          <TabsContent value="best-time">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Best Time to Post
                </CardTitle>
              </CardHeader>
              <CardContent>
                {publishedPosts.filter(p => postEngagement(p) > 0).length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    Need published posts with engagement data to calculate best times.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="text-xs text-muted-foreground mb-3">
                      Average engagement by day and hour. Darker = higher engagement.
                    </div>
                    {/* Hour headers */}
                    <div className="flex gap-0.5 mb-1 ml-[52px]">
                      {HOURS.map(h => (
                        <div key={h} className="w-9 text-[10px] text-center text-muted-foreground">{h}</div>
                      ))}
                    </div>
                    {/* Day rows */}
                    {DAYS.map(day => {
                      const maxVal = Math.max(...Object.values(bestTimeData[day] || {}), 1);
                      return (
                        <div key={day} className="flex items-center gap-0.5 mb-0.5">
                          <div className="w-[48px] text-xs font-medium text-foreground text-right pr-1">{day}</div>
                          {HOURS.map(hour => {
                            const val = bestTimeData[day]?.[hour] || 0;
                            const intensity = val > 0 ? Math.max(0.15, val / maxVal) : 0;
                            return (
                              <div
                                key={hour}
                                className="w-9 h-7 rounded-sm flex items-center justify-center text-[10px]"
                                style={{
                                  backgroundColor: val > 0
                                    ? `rgba(34, 197, 94, ${intensity})`
                                    : 'hsl(var(--muted))',
                                  color: intensity > 0.5 ? 'white' : 'hsl(var(--muted-foreground))',
                                }}
                                title={`${day} ${hour}: avg ${val} engagements`}
                              >
                                {val > 0 ? val : ''}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Posting Cadence Tab ── */}
          <TabsContent value="cadence">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Posting Cadence</CardTitle>
              </CardHeader>
              <CardContent>
                {cadence.weeks.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">No published posts to show cadence.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="flex gap-1 mb-2 ml-[160px]">
                      {cadence.weeks.map(w => (
                        <div key={w} className="w-10 text-xs text-center text-muted-foreground">{w}</div>
                      ))}
                    </div>
                    {PLATFORMS.map(p => {
                      const cfg = PLATFORM_CONFIG[p];
                      const Icon = cfg.icon;
                      const row = cadence.data[p] || {};
                      return (
                        <div key={p} className="flex items-center gap-1 mb-1">
                          <div className="w-[160px] flex items-center gap-2 shrink-0">
                            <Icon className={`h-4 w-4 ${cfg.text}`} />
                            <span className="text-sm text-foreground truncate">{cfg.label}</span>
                          </div>
                          {cadence.weeks.map(w => {
                            const count = row[w] || 0;
                            return (
                              <div
                                key={w}
                                className={`w-10 h-8 rounded flex items-center justify-center text-xs font-medium ${
                                  count === 0
                                    ? 'bg-muted text-muted-foreground'
                                    : `${cfg.bg} ${cfg.text} ${cfg.border} border`
                                }`}
                                title={`${cfg.label}: ${count} posts (week of ${w})`}
                              >
                                {count || ''}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Manual Entry Dialog ── */}
      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Channel Metrics</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Platform</Label>
                <Select value={entryPlatform} onValueChange={setEntryPlatform}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => (
                      <SelectItem key={p} value={p}>{PLATFORM_CONFIG[p].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(['followers', 'impressions', 'reach', 'likes', 'comments', 'shares', 'clicks'] as const).map(field => (
                <div key={field}>
                  <Label className="capitalize">{field}</Label>
                  <Input type="number" placeholder="0" value={entryFields[field]} onChange={e => setEntryFields(prev => ({ ...prev, [field]: e.target.value }))} />
                </div>
              ))}
              <div>
                <Label>Engagement Rate %</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={entryFields.engagement_rate} onChange={e => setEntryFields(prev => ({ ...prev, engagement_rate: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryOpen(false)}>Cancel</Button>
            <Button onClick={saveMetrics} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
